import { useCallback, useEffect, useMemo, useState } from "react";
import { createAdminClientViewSponsorshipApi } from "../../../../api/sponsorshipSectionApi";

const SUB_TABS = [
  { id: "overview", label: "Overview" },
  { id: "inventory", label: "Inventory Slots" },
  { id: "placements", label: "Placements" },
  { id: "campaigns", label: "Campaigns" },
];

const SLOT_TYPES = [
  { value: "sponsored_listing", label: "Sponsored Listing", icon: "🏠" },
  { value: "featured_community", label: "Featured Community", icon: "🏘️" },
  { value: "featured_agent", label: "Featured Agent", icon: "👤" },
  { value: "vendor_card", label: "Vendor Card", icon: "🏪" },
  { value: "banner_ad", label: "Banner Ad", icon: "🖼️" },
  { value: "native_card", label: "Native Card", icon: "📰" },
  { value: "sidebar_ad", label: "Sidebar Ad", icon: "📌" },
  { value: "search_result_ad", label: "Search Result Ad", icon: "🔍" },
  { value: "listing_detail_ad", label: "Listing Detail Ad", icon: "📋" },
  { value: "community_page_ad", label: "Community Page Ad", icon: "📍" },
  { value: "other", label: "Other", icon: "📦" },
];

const PAGE_CONTEXTS = [
  { value: "home", label: "Home" },
  { value: "search", label: "Search" },
  { value: "listing_detail", label: "Listing Detail" },
  { value: "community", label: "Community" },
  { value: "contact", label: "Contact" },
  { value: "content", label: "Content" },
  { value: "global", label: "Global" },
  { value: "other", label: "Other" },
];

const PLACEMENT_REGIONS = [
  { value: "hero", label: "Hero" },
  { value: "inline_results", label: "Inline Results" },
  { value: "sidebar", label: "Sidebar" },
  { value: "footer", label: "Footer" },
  { value: "card_grid", label: "Card Grid" },
  { value: "detail_sidebar", label: "Detail Sidebar" },
  { value: "detail_below_gallery", label: "Below Gallery" },
  { value: "community_header", label: "Community Header" },
  { value: "content_inline", label: "Content Inline" },
  { value: "other", label: "Other" },
];

function slotTypeIcon(slotType) {
  return SLOT_TYPES.find((t) => t.value === slotType)?.icon || "📦";
}

function slotTypeLabel(slotType) {
  return SLOT_TYPES.find((t) => t.value === slotType)?.label || slotType;
}

function statusTone(status) {
  if (status === "active") return "green";
  if (status === "paused") return "amber";
  if (status === "draft") return "gray";
  if (status === "completed") return "blue";
  return "gray";
}

/* ── Overview pane ── */

function OverviewPane({ slots, placements, campaigns }) {
  const activeSlots = slots.filter((s) => s.isActive);
  const activePlacements = placements.filter((p) => p.status === "active");
  const draftPlacements = placements.filter((p) => p.status === "draft");
  const activeCampaigns = campaigns.filter((c) => c.status === "active");

  const bySlotType = {};
  slots.forEach((s) => {
    bySlotType[s.slotType] = (bySlotType[s.slotType] || 0) + 1;
  });
  const slotTypeEntries = Object.entries(bySlotType).sort((a, b) => b[1] - a[1]);
  const maxSlotCount = Math.max(...Object.values(bySlotType), 1);

  const byContext = {};
  slots.forEach((s) => {
    byContext[s.pageContext] = (byContext[s.pageContext] || 0) + 1;
  });

  return (
    <div className="cv-sponsor__pane">
      {/* KPI row */}
      <div className="cv-sponsor__kpi-row">
        <div className="cv-sponsor__kpi">
          <span className="cv-sponsor__kpi-icon">📦</span>
          <div className="cv-sponsor__kpi-body">
            <span className="cv-sponsor__kpi-label">Inventory Slots</span>
            <span className="cv-sponsor__kpi-value">{slots.length}</span>
            <span className="cv-sponsor__kpi-sub">{activeSlots.length} active</span>
          </div>
        </div>
        <div className="cv-sponsor__kpi">
          <span className="cv-sponsor__kpi-icon">📍</span>
          <div className="cv-sponsor__kpi-body">
            <span className="cv-sponsor__kpi-label">Placements</span>
            <span className="cv-sponsor__kpi-value">{placements.length}</span>
            <span className="cv-sponsor__kpi-sub">{activePlacements.length} active, {draftPlacements.length} draft</span>
          </div>
        </div>
        <div className="cv-sponsor__kpi">
          <span className="cv-sponsor__kpi-icon">📣</span>
          <div className="cv-sponsor__kpi-body">
            <span className="cv-sponsor__kpi-label">Campaigns</span>
            <span className="cv-sponsor__kpi-value">{campaigns.length}</span>
            <span className="cv-sponsor__kpi-sub">{activeCampaigns.length} active</span>
          </div>
        </div>
        <div className="cv-sponsor__kpi">
          <span className="cv-sponsor__kpi-icon">📊</span>
          <div className="cv-sponsor__kpi-body">
            <span className="cv-sponsor__kpi-label">Fill Rate</span>
            <span className="cv-sponsor__kpi-value">{activeSlots.length > 0 ? `${Math.round((activePlacements.length / activeSlots.length) * 100)}%` : "—"}</span>
            <span className="cv-sponsor__kpi-sub">{activePlacements.length} / {activeSlots.length} slots filled</span>
          </div>
        </div>
      </div>

      <div className="cv-sponsor__two-col">
        {/* By slot type */}
        <section className="cv-sponsor__card">
          <h4 className="cv-sponsor__card-title">Slots by Type</h4>
          {slotTypeEntries.length === 0 ? <p className="cv-sponsor__empty-text">No slots created yet.</p> : (
            <div className="cv-sponsor__bar-list">
              {slotTypeEntries.map(([type, count]) => {
                const pctVal = Math.round((count / maxSlotCount) * 100);
                return (
                  <div key={type} className="cv-sponsor__bar-item">
                    <div className="cv-sponsor__bar-header">
                      <span className="cv-sponsor__bar-icon">{slotTypeIcon(type)}</span>
                      <span className="cv-sponsor__bar-label">{slotTypeLabel(type)}</span>
                      <span className="cv-sponsor__bar-count">{count}</span>
                    </div>
                    <div className="cv-sponsor__bar-track">
                      <div className="cv-sponsor__bar-fill" style={{ width: `${pctVal}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* By page context */}
        <section className="cv-sponsor__card">
          <h4 className="cv-sponsor__card-title">Slots by Page Context</h4>
          {Object.keys(byContext).length === 0 ? <p className="cv-sponsor__empty-text">No context data.</p> : (
            <div className="cv-sponsor__context-grid">
              {Object.entries(byContext).sort((a, b) => b[1] - a[1]).map(([ctx, count]) => (
                <div key={ctx} className="cv-sponsor__context-chip">
                  <span className="cv-sponsor__context-label">{ctx.replace(/_/g, " ")}</span>
                  <span className="cv-sponsor__context-count">{count}</span>
                </div>
              ))}
            </div>
          )}

          {/* Recent placements */}
          <h4 className="cv-sponsor__card-title" style={{ marginTop: 20 }}>Recent Placements</h4>
          {placements.length === 0 ? <p className="cv-sponsor__empty-text">No placements yet.</p> : (
            <div className="cv-sponsor__mini-list">
              {placements.slice(0, 5).map((p) => (
                <div key={p.id} className="cv-sponsor__mini-item">
                  <span className="cv-sponsor__mini-name">{p.placementName || "(unnamed)"}</span>
                  <span className={`cv-sponsor__status-dot cv-sponsor__status-dot--${statusTone(p.status)}`} />
                  <span className="cv-sponsor__mini-status">{p.status}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

/* ── Inventory pane ── */

function InventoryPane({ slots, canManage, onRefresh, api }) {
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({
    slotKey: "", name: "", slotType: "sponsored_listing", pageContext: "search", placementRegion: "inline_results", maxActivePlacements: 1,
  });
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    setSaving(true);
    try {
      await api.createInventorySlot(form);
      setForm({ slotKey: "", name: "", slotType: "sponsored_listing", pageContext: "search", placementRegion: "inline_results", maxActivePlacements: 1 });
      setFormOpen(false);
      await onRefresh();
    } finally { setSaving(false); }
  };

  const handleToggle = async (slot) => {
    await api.patchInventorySlotStatus(slot.id, !slot.isActive);
    await onRefresh();
  };

  return (
    <div className="cv-sponsor__pane">
      <div className="cv-sponsor__pane-header">
        <div>
          <h3 className="cv-sponsor__pane-title">Inventory Slots</h3>
          <p className="cv-sponsor__pane-desc">Define ad positions across your client view pages. Each slot can hold one or more active placements.</p>
        </div>
        {canManage ? <button type="button" className="cv-sponsor__add-btn" onClick={() => setFormOpen(!formOpen)}>{formOpen ? "Cancel" : "+ Add slot"}</button> : null}
      </div>

      {/* Create form */}
      {formOpen ? (
        <div className="cv-sponsor__create-card">
          <h4 className="cv-sponsor__create-title">New Inventory Slot</h4>
          <div className="cv-sponsor__create-grid">
            <label className="cv-sponsor__field">
              <span className="cv-sponsor__field-label">Slot Key</span>
              <input className="cv-sponsor__field-input" value={form.slotKey} onChange={(e) => setForm({ ...form, slotKey: e.target.value })} placeholder="e.g. search-top-banner" />
            </label>
            <label className="cv-sponsor__field">
              <span className="cv-sponsor__field-label">Name</span>
              <input className="cv-sponsor__field-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Search Top Banner" />
            </label>
            <label className="cv-sponsor__field">
              <span className="cv-sponsor__field-label">Slot Type</span>
              <select className="cv-sponsor__field-select" value={form.slotType} onChange={(e) => setForm({ ...form, slotType: e.target.value })}>
                {SLOT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
              </select>
            </label>
            <label className="cv-sponsor__field">
              <span className="cv-sponsor__field-label">Page Context</span>
              <select className="cv-sponsor__field-select" value={form.pageContext} onChange={(e) => setForm({ ...form, pageContext: e.target.value })}>
                {PAGE_CONTEXTS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </label>
            <label className="cv-sponsor__field">
              <span className="cv-sponsor__field-label">Placement Region</span>
              <select className="cv-sponsor__field-select" value={form.placementRegion} onChange={(e) => setForm({ ...form, placementRegion: e.target.value })}>
                {PLACEMENT_REGIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </label>
            <label className="cv-sponsor__field">
              <span className="cv-sponsor__field-label">Max Active Placements</span>
              <input className="cv-sponsor__field-input" type="number" min="1" value={form.maxActivePlacements} onChange={(e) => setForm({ ...form, maxActivePlacements: Number(e.target.value) })} />
            </label>
          </div>
          <div className="cv-sponsor__create-actions">
            <button type="button" className="cv-sponsor__add-btn" disabled={!form.slotKey || !form.name || saving} onClick={handleCreate}>{saving ? "Creating\u2026" : "Create Slot"}</button>
            <button type="button" className="cv-sponsor__cancel-btn" onClick={() => setFormOpen(false)}>Cancel</button>
          </div>
        </div>
      ) : null}

      {/* Slot list */}
      {slots.length === 0 && !formOpen ? (
        <div className="cv-sponsor__empty-state">
          <span className="cv-sponsor__empty-icon">📦</span>
          <p className="cv-sponsor__empty-title">No inventory slots</p>
          <p className="cv-sponsor__empty-desc">Create your first slot to define where sponsored content appears on your site.</p>
        </div>
      ) : (
        <div className="cv-sponsor__slot-list">
          {slots.map((slot) => (
            <div key={slot.id} className={`cv-sponsor__slot-card${slot.isActive ? "" : " cv-sponsor__slot-card--inactive"}`}>
              <div className="cv-sponsor__slot-card-header">
                <span className="cv-sponsor__slot-icon">{slotTypeIcon(slot.slotType)}</span>
                <div className="cv-sponsor__slot-info">
                  <span className="cv-sponsor__slot-name">{slot.name}</span>
                  <code className="cv-sponsor__slot-key">{slot.slotKey}</code>
                </div>
                <span className={`cv-sponsor__status-badge cv-sponsor__status-badge--${slot.isActive ? "green" : "gray"}`}>{slot.isActive ? "Active" : "Inactive"}</span>
                {canManage ? (
                  <button type="button" className="cv-sponsor__toggle-btn" onClick={() => handleToggle(slot)}>{slot.isActive ? "Deactivate" : "Activate"}</button>
                ) : null}
              </div>
              <div className="cv-sponsor__slot-meta">
                <span className="cv-sponsor__meta-chip">{slotTypeLabel(slot.slotType)}</span>
                <span className="cv-sponsor__meta-chip">{(slot.pageContext || "—").replace(/_/g, " ")}</span>
                <span className="cv-sponsor__meta-chip">{(slot.placementRegion || "—").replace(/_/g, " ")}</span>
                {slot.maxActivePlacements ? <span className="cv-sponsor__meta-chip">Max {slot.maxActivePlacements}</span> : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Placements pane ── */

function PlacementsPane({ slots, placements, canManage, onRefresh, api, partnerAccounts = [] }) {
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({ inventorySlotId: "", partnerAccountId: "", placementName: "", headline: "", targetUrl: "", status: "draft" });
  const [saving, setSaving] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const filtered = statusFilter === "all" ? placements : placements.filter((p) => p.status === statusFilter);

  const handleCreate = async () => {
    setSaving(true);
    try {
      await api.createPlacement(form);
      setForm({ inventorySlotId: "", partnerAccountId: "", placementName: "", headline: "", targetUrl: "", status: "draft" });
      setFormOpen(false);
      await onRefresh();
    } finally { setSaving(false); }
  };

  const handleStatusToggle = async (p) => {
    const next = p.status === "active" ? "paused" : "active";
    await api.patchPlacementStatus(p.id, next);
    await onRefresh();
  };

  const handlePreview = async (id) => {
    if (previewId === id) { setPreviewData(null); setPreviewId(null); return; }
    const body = await api.previewPlacement(id);
    setPreviewData(body);
    setPreviewId(id);
  };

  const slotName = (slotId) => {
    const s = slots.find((slot) => slot.id === slotId);
    return s ? s.name : slotId;
  };

  return (
    <div className="cv-sponsor__pane">
      <div className="cv-sponsor__pane-header">
        <div>
          <h3 className="cv-sponsor__pane-title">Placements</h3>
          <p className="cv-sponsor__pane-desc">Schedule and manage sponsored content across your inventory slots.</p>
        </div>
        {canManage ? <button type="button" className="cv-sponsor__add-btn" onClick={() => setFormOpen(!formOpen)}>{formOpen ? "Cancel" : "+ Add placement"}</button> : null}
      </div>

      {/* Status filter */}
      <div className="cv-sponsor__filters">
        {["all", "draft", "active", "paused"].map((f) => (
          <button key={f} type="button" className={`cv-sponsor__filter${statusFilter === f ? " cv-sponsor__filter--active" : ""}`} onClick={() => setStatusFilter(f)}>
            {f === "all" ? `All (${placements.length})` : `${f.charAt(0).toUpperCase() + f.slice(1)} (${placements.filter((p) => p.status === f).length})`}
          </button>
        ))}
      </div>

      {/* Create form */}
      {formOpen ? (
        <div className="cv-sponsor__create-card">
          <h4 className="cv-sponsor__create-title">New Placement</h4>
          <div className="cv-sponsor__create-grid">
            <label className="cv-sponsor__field">
              <span className="cv-sponsor__field-label">Inventory Slot</span>
              <select className="cv-sponsor__field-select" value={form.inventorySlotId} onChange={(e) => setForm({ ...form, inventorySlotId: e.target.value })}>
                <option value="">Select slot</option>
                {slots.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.slotKey})</option>)}
              </select>
            </label>
            <label className="cv-sponsor__field">
              <span className="cv-sponsor__field-label">Partner account</span>
              {partnerAccounts.length ? (
                <select
                  className="cv-sponsor__field-select"
                  value={form.partnerAccountId}
                  onChange={(e) => setForm({ ...form, partnerAccountId: e.target.value })}
                >
                  <option value="">Select partner</option>
                  {partnerAccounts.map((partner) => (
                    <option key={partner.partnerAccountId} value={partner.partnerAccountId}>
                      {partner.displayName || partner.businessName || partner.email}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className="cv-sponsor__field-input"
                  value={form.partnerAccountId}
                  onChange={(e) => setForm({ ...form, partnerAccountId: e.target.value })}
                  placeholder="Partner or advertiser ID"
                />
              )}
            </label>
            <label className="cv-sponsor__field">
              <span className="cv-sponsor__field-label">Placement Name</span>
              <input className="cv-sponsor__field-input" value={form.placementName} onChange={(e) => setForm({ ...form, placementName: e.target.value })} placeholder="e.g. Acme Realty - Search Banner" />
            </label>
            <label className="cv-sponsor__field">
              <span className="cv-sponsor__field-label">Headline</span>
              <input className="cv-sponsor__field-input" value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} placeholder="Ad headline text" />
            </label>
            <label className="cv-sponsor__field cv-sponsor__field--wide">
              <span className="cv-sponsor__field-label">Target URL</span>
              <input className="cv-sponsor__field-input" value={form.targetUrl} onChange={(e) => setForm({ ...form, targetUrl: e.target.value })} placeholder="https://example.com/landing" />
            </label>
          </div>
          <div className="cv-sponsor__create-actions">
            <button type="button" className="cv-sponsor__add-btn" disabled={!form.placementName || !form.inventorySlotId || saving} onClick={handleCreate}>{saving ? "Creating\u2026" : "Create Placement"}</button>
            <button type="button" className="cv-sponsor__cancel-btn" onClick={() => setFormOpen(false)}>Cancel</button>
          </div>
        </div>
      ) : null}

      {/* Placement list */}
      {filtered.length === 0 ? (
        <div className="cv-sponsor__empty-state">
          <span className="cv-sponsor__empty-icon">📍</span>
          <p className="cv-sponsor__empty-title">No placements{statusFilter !== "all" ? ` with status "${statusFilter}"` : ""}</p>
          <p className="cv-sponsor__empty-desc">Create a placement to schedule sponsored content in your inventory slots.</p>
        </div>
      ) : (
        <div className="cv-sponsor__placement-list">
          {filtered.map((p) => (
            <div key={p.id} className="cv-sponsor__placement-card">
              <div className="cv-sponsor__placement-header">
                <div className="cv-sponsor__placement-info">
                  <span className="cv-sponsor__placement-name">{p.placementName || "(unnamed)"}</span>
                  {p.headline ? <span className="cv-sponsor__placement-headline">{p.headline}</span> : null}
                </div>
                <span className={`cv-sponsor__status-badge cv-sponsor__status-badge--${statusTone(p.status)}`}>{p.status}</span>
              </div>
              <div className="cv-sponsor__placement-meta">
                <span className="cv-sponsor__meta-chip">Slot: {slotName(p.inventorySlotId)}</span>
                {p.slotKey ? <span className="cv-sponsor__meta-chip">{p.slotKey}</span> : null}
                {p.partnerAccountId ? <span className="cv-sponsor__meta-chip">Partner: {p.partnerAccountId}</span> : null}
                {p.targetUrl ? <span className="cv-sponsor__meta-chip cv-sponsor__meta-chip--link" title={p.targetUrl}>{new URL(p.targetUrl, "https://x").hostname}</span> : null}
              </div>
              {canManage ? (
                <div className="cv-sponsor__placement-actions">
                  <button type="button" className="cv-sponsor__action-btn" onClick={() => handlePreview(p.id)}>{previewId === p.id ? "Hide preview" : "Preview"}</button>
                  <button type="button" className="cv-sponsor__action-btn" onClick={() => handleStatusToggle(p)}>{p.status === "active" ? "Pause" : "Activate"}</button>
                </div>
              ) : null}
              {previewId === p.id && previewData ? (
                <div className="cv-sponsor__preview-box">
                  <div className="cv-sponsor__preview-status">
                    <span className={`cv-sponsor__status-badge cv-sponsor__status-badge--${previewData.eligibility === "eligible" ? "green" : "amber"}`}>{previewData.eligibility}</span>
                    {(previewData.warnings || []).map((w) => <span key={w.code} className="cv-sponsor__preview-warn">{w.message}</span>)}
                  </div>
                  {previewData.preview ? (
                    <div className="cv-sponsor__preview-content">
                      <span className="cv-sponsor__preview-label">Sponsored</span>
                      <h5 className="cv-sponsor__preview-headline">{previewData.preview.headline}</h5>
                      {previewData.preview.bodyText ? <p className="cv-sponsor__preview-body">{previewData.preview.bodyText}</p> : null}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Campaigns pane ── */

function CampaignsPane({ campaigns, canManage, onRefresh, api, partnerAccounts = [] }) {
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({
    campaignName: "",
    partnerAccountId: "",
    objective: "awareness",
    status: "draft",
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const handleCreate = async () => {
    setSaving(true);
    setFormError("");
    try {
      await api.createCampaign(form);
      setForm({ campaignName: "", partnerAccountId: "", objective: "awareness", status: "draft" });
      setFormOpen(false);
      await onRefresh();
    } catch (err) {
      setFormError(err.message || "Unable to create campaign.");
    } finally {
      setSaving(false);
    }
  };

  const statusGroups = { active: [], draft: [], completed: [], paused: [] };
  campaigns.forEach((c) => {
    const bucket = statusGroups[c.status] || statusGroups.draft;
    bucket.push(c);
  });

  return (
    <div className="cv-sponsor__pane">
      <div className="cv-sponsor__pane-header">
        <div>
          <h3 className="cv-sponsor__pane-title">Campaigns</h3>
          <p className="cv-sponsor__pane-desc">Manage advertising campaigns and link them to placements for billing and performance tracking.</p>
        </div>
        {canManage ? <button type="button" className="cv-sponsor__add-btn" onClick={() => setFormOpen(!formOpen)}>{formOpen ? "Cancel" : "+ Add campaign"}</button> : null}
      </div>

      {/* Create form */}
      {formOpen ? (
        <div className="cv-sponsor__create-card">
          <h4 className="cv-sponsor__create-title">New Campaign</h4>
          <div className="cv-sponsor__create-grid">
            <label className="cv-sponsor__field">
              <span className="cv-sponsor__field-label">Campaign Name</span>
              <input className="cv-sponsor__field-input" value={form.campaignName} onChange={(e) => setForm({ ...form, campaignName: e.target.value })} placeholder="e.g. Q2 Agent Spotlight" />
            </label>
            <label className="cv-sponsor__field">
              <span className="cv-sponsor__field-label">Partner account</span>
              {partnerAccounts.length ? (
                <select
                  className="cv-sponsor__field-select"
                  value={form.partnerAccountId}
                  onChange={(e) => setForm({ ...form, partnerAccountId: e.target.value })}
                >
                  <option value="">Select partner</option>
                  {partnerAccounts.map((partner) => (
                    <option key={partner.partnerAccountId} value={partner.partnerAccountId}>
                      {partner.displayName || partner.businessName || partner.email}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className="cv-sponsor__field-input"
                  value={form.partnerAccountId}
                  onChange={(e) => setForm({ ...form, partnerAccountId: e.target.value })}
                  placeholder="Partner account ID"
                />
              )}
            </label>
            <label className="cv-sponsor__field">
              <span className="cv-sponsor__field-label">Objective</span>
              <select className="cv-sponsor__field-select" value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })}>
                <option value="awareness">Awareness</option>
                <option value="traffic">Traffic</option>
                <option value="lead_generation">Lead generation</option>
                <option value="listing_promotion">Listing promotion</option>
                <option value="community_promotion">Community promotion</option>
                <option value="vendor_promotion">Vendor promotion</option>
                <option value="other">Other</option>
              </select>
            </label>
          </div>
          {formError ? <p className="cv-sponsor__error">{formError}</p> : null}
          <div className="cv-sponsor__create-actions">
            <button
              type="button"
              className="cv-sponsor__add-btn"
              disabled={!form.campaignName || !form.partnerAccountId || saving}
              onClick={handleCreate}
            >
              {saving ? "Creating\u2026" : "Create Campaign"}
            </button>
            <button type="button" className="cv-sponsor__cancel-btn" onClick={() => setFormOpen(false)}>Cancel</button>
          </div>
        </div>
      ) : null}

      {campaigns.length === 0 && !formOpen ? (
        <div className="cv-sponsor__empty-state">
          <span className="cv-sponsor__empty-icon">📣</span>
          <p className="cv-sponsor__empty-title">No campaigns</p>
          <p className="cv-sponsor__empty-desc">Create a campaign to organize and track your sponsorship activity.</p>
        </div>
      ) : (
        <div className="cv-sponsor__campaign-groups">
          {Object.entries(statusGroups).filter(([, list]) => list.length > 0).map(([status, list]) => (
            <div key={status} className="cv-sponsor__campaign-group">
              <h4 className="cv-sponsor__group-title">
                <span className={`cv-sponsor__status-dot cv-sponsor__status-dot--${statusTone(status)}`} />
                {status.charAt(0).toUpperCase() + status.slice(1)} ({list.length})
              </h4>
              <div className="cv-sponsor__campaign-list">
                {list.map((c) => (
                  <div key={c.id} className="cv-sponsor__campaign-card">
                    <div className="cv-sponsor__campaign-header">
                      <span className="cv-sponsor__campaign-name">{c.campaignName}</span>
                      <span className={`cv-sponsor__status-badge cv-sponsor__status-badge--${statusTone(c.status)}`}>{c.status}</span>
                    </div>
                    <div className="cv-sponsor__campaign-meta">
                      <span className="cv-sponsor__meta-chip">Objective: {c.objective || "—"}</span>
                      {c.impressions != null ? <span className="cv-sponsor__meta-chip">{Number(c.impressions).toLocaleString()} impressions</span> : null}
                      {c.clicks != null ? <span className="cv-sponsor__meta-chip">{Number(c.clicks).toLocaleString()} clicks</span> : null}
                      {c.ctr != null ? <span className="cv-sponsor__meta-chip">CTR {(c.ctr * 100).toFixed(1)}%</span> : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Main component ── */

const StudioSponsorshipSection = ({ clientViewId, canManage, api: apiProp, initialSubTab = "overview" }) => {
  const api = useMemo(
    () => apiProp || createAdminClientViewSponsorshipApi(clientViewId),
    [apiProp, clientViewId]
  );
  const [activeSubTab, setActiveSubTab] = useState(initialSubTab);
  const [slots, setSlots] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [partnerAccounts, setPartnerAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const [slotBody, placementBody, campaignBody, partnerBody] = await Promise.all([
        api.listInventorySlots(),
        api.listPlacements(),
        api.listCampaigns(),
        api.listPartners?.() || Promise.resolve({ partners: [] }),
      ]);
      setSlots(slotBody.slots || []);
      setPlacements(placementBody.placements || []);
      setCampaigns(campaignBody.campaigns || []);
      setPartnerAccounts(partnerBody.partners || []);
    } catch (e) {
      setError(e.message);
      setSlots([]);
      setPlacements([]);
      setCampaigns([]);
      setPartnerAccounts([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="cv-sponsor">
      <nav className="cv-sponsor__tabs" role="tablist">
        {SUB_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeSubTab === tab.id}
            className={`cv-sponsor__tab${activeSubTab === tab.id ? " cv-sponsor__tab--active" : ""}`}
            onClick={() => setActiveSubTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {error ? <div className="cv-sponsor__error">{error}</div> : null}

      {loading ? (
        <div className="cv-sponsor__loading">Loading sponsorship data&hellip;</div>
      ) : (
        <>
          {activeSubTab === "overview" ? <OverviewPane slots={slots} placements={placements} campaigns={campaigns} /> : null}
          {activeSubTab === "inventory" ? <InventoryPane slots={slots} canManage={canManage} onRefresh={load} api={api} /> : null}
          {activeSubTab === "placements" ? <PlacementsPane slots={slots} placements={placements} canManage={canManage} onRefresh={load} api={api} partnerAccounts={partnerAccounts} /> : null}
          {activeSubTab === "campaigns" ? (
            <CampaignsPane
              campaigns={campaigns}
              canManage={canManage}
              onRefresh={load}
              api={api}
              partnerAccounts={partnerAccounts}
            />
          ) : null}
        </>
      )}
    </div>
  );
};

export default StudioSponsorshipSection;
